import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { customersApi, playgroundApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { ConfidenceBadge, ErrorNote, MatchBadge, PageHeader } from '@/components/common/Ui'
import { money, percent } from '@/lib/format'

const TIERS = ['A', 'A+', 'B', 'B+', 'P']
const ACCEPT = 'image/*,audio/*,.ogg,.opus,.m4a,.amr'

/**
 * Upload a slip photo or voice note and watch every pipeline stage.
 *
 * This is the tuning surface: it runs the real OCR / transcription /
 * extraction / matching code but writes nothing, so you can judge quality on
 * genuine customer messages without creating junk orders.
 */
export default function Playground() {
  const [file, setFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [tier, setTier] = useState('A')
  const [customerId, setCustomerId] = useState('')
  const [ocrEngine, setOcrEngine] = useState('')
  const [stt, setStt] = useState('')
  const [extraction, setExtraction] = useState('')
  const [whisperModel, setWhisperModel] = useState('')
  const [sarvamMode, setSarvamMode] = useState('')
  const [language, setLanguage] = useState('')
  const [withTimestamps, setWithTimestamps] = useState(false)
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef(null)

  const { data: customers } = useQuery({
    queryKey: ['customers', { page_size: 200 }],
    queryFn: () => customersApi.list({ page_size: 200 }),
  })

  const run = useMutation({
    mutationFn: (opts) =>
      playgroundApi.extract(opts.file, {
        price_tier: opts.tier,
        customer_id: opts.customerId || undefined,
        ocr_engine: opts.ocrEngine || undefined,
        stt: opts.stt || undefined,
        extraction_override: opts.extraction || undefined,
        whisper_model: opts.whisperModel || undefined,
        sarvam_mode: opts.sarvamMode || undefined,
        language: opts.language || undefined,
        with_timestamps: opts.withTimestamps || undefined,
      }),
  })

  // Object URLs must be revoked or each upload leaks the previous blob.
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const choose = (next) => {
    if (!next) return
    setFile(next)
    run.reset()
  }

  const onDrop = (event) => {
    event.preventDefault()
    setDragging(false)
    choose(event.dataTransfer.files?.[0])
  }

  const isImage = file?.type?.startsWith('image/')
  const isAudio = file?.type?.startsWith('audio/') || /\.(ogg|opus|m4a|amr)$/i.test(file?.name ?? '')
  const result = run.data

  return (
    <div className="playground">
      <PageHeader
        title="Test bench"
        subtitle="Upload a slip photo or voice note and see what the pipeline reads. Nothing is saved."
      />

      <div className="bench-grid">
        {/* ---------------- input side ---------------- */}
        <section className="panel">
          <div className="panel-title">1 · Upload</div>

          <div
            className={`dropzone${dragging ? ' dragging' : ''}${file ? ' has-file' : ''}`}
            onDragOver={(e) => {
              e.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
          >
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              hidden
              onChange={(e) => choose(e.target.files?.[0])}
            />
            {!file ? (
              <>
                <div className="drop-icon">📄🎤</div>
                <div className="drop-title">Drop a photo or voice note</div>
                <div className="drop-hint">or click to choose · JPEG, PNG, OGG, MP3, M4A</div>
              </>
            ) : (
              <>
                <div className="drop-title">{file.name}</div>
                <div className="drop-hint">
                  {(file.size / 1024).toFixed(0)} KB · {file.type || 'unknown type'}
                </div>
                <div className="drop-hint">Click to choose a different file</div>
              </>
            )}
          </div>

          {previewUrl && isImage && (
            <figure className="bench-preview">
              <img src={previewUrl} alt="Uploaded slip" />
            </figure>
          )}
          {previewUrl && isAudio && (
            <div className="bench-preview">
              <audio controls src={previewUrl} style={{ width: '100%' }} />
            </div>
          )}

          <div className="bench-controls">
            <label className="field">
              <span>Price as customer</span>
              <select
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
              >
                <option value="">— use tier below —</option>
                {customers?.items?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.price_tier})
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>Reading orders</span>
              <select
                value={extraction}
                onChange={(e) => setExtraction(e.target.value)}
              >
                <option value="">Configured default</option>
                <option value="llm">LLM — reads unfamiliar phrasing</option>
                <option value="rules">Rules — local, no API call</option>
              </select>
            </label>

            <label className="field">
              <span>Price tier</span>
              <select
                value={tier}
                onChange={(e) => setTier(e.target.value)}
                disabled={Boolean(customerId)}
              >
                {TIERS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {isImage && (
            <div className="bench-controls">
              <label className="field">
                <span>OCR engine</span>
                <select
                  value={ocrEngine}
                  onChange={(e) => setOcrEngine(e.target.value)}
                >
                  <option value="">Configured default</option>
                  <option value="sarvam">Sarvam Document AI — reads handwriting</option>
                </select>
              </label>

              <label className="field">
                <span>Language</span>
                <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                  <option value="">Default</option>
                  <option value="ta-IN">Tamil</option>
                  <option value="en-IN">English</option>
                  <option value="hi-IN">Hindi</option>
                  <option value="te-IN">Telugu</option>
                  <option value="ml-IN">Malayalam</option>
                  <option value="kn-IN">Kannada</option>
                </select>
              </label>
            </div>
          )}

          {isAudio && (
            <>
              <div className="bench-controls">
                <label className="field">
                  <span>Speech engine</span>
                  <select value={stt} onChange={(e) => setStt(e.target.value)}>
                    <option value="">Configured default</option>
                    <option value="sarvam">Sarvam AI — best for Indic</option>
                    <option value="faster_whisper">Whisper (local)</option>
                  </select>
                </label>

                <label className="field">
                  <span>Language</span>
                  <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                    <option value="">Auto-detect</option>
                    <option value="ta">Tamil</option>
                    <option value="en">English</option>
                    <option value="hi">Hindi</option>
                    <option value="te">Telugu</option>
                    <option value="ml">Malayalam</option>
                    <option value="kn">Kannada</option>
                  </select>
                </label>
              </div>

              {stt === 'sarvam' && (
                <label className="field">
                  <span>Sarvam mode</span>
                  <select
                    value={sarvamMode}
                    onChange={(e) => setSarvamMode(e.target.value)}
                  >
                    <option value="">Configured default</option>
                    <option value="transcribe">transcribe — keep spoken language</option>
                    <option value="codemix">codemix — Tamil + English mixed</option>
                    <option value="translate">translate — return English</option>
                    <option value="verbatim">verbatim</option>
                    <option value="translit">translit — Tamil in Latin script</option>
                  </select>
                </label>
              )}

              {stt === 'sarvam' && (
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={withTimestamps}
                    onChange={(e) => setWithTimestamps(e.target.checked)}
                  />
                  <span>Request word timestamps</span>
                </label>
              )}

              {stt === 'faster_whisper' && (
                <label className="field">
                  <span>Whisper model</span>
                  <select
                    value={whisperModel}
                    onChange={(e) => setWhisperModel(e.target.value)}
                  >
                    <option value="">Configured default</option>
                    <option value="tiny">tiny — fastest, least accurate</option>
                    <option value="base">base</option>
                    <option value="small">small</option>
                    <option value="medium">medium — drifts on Tamil, avoid</option>
                    <option value="large-v3">large-v3 — best local for Tamil</option>
                  </select>
                </label>
              )}
            </>
          )}

          <button
            type="button"
            className="btn btn-primary btn-block"
            disabled={!file || run.isPending}
            onClick={() =>
              run.mutate({
                file, tier, customerId, ocrEngine, stt, whisperModel, extraction,
                sarvamMode, language, withTimestamps,
              })
            }
          >
            {run.isPending ? 'Running pipeline…' : 'Run pipeline'}
          </button>

          {run.isPending && (
            <p className="hint">
              OCR and transcription run locally and take a few seconds on first
              use while the models load.
            </p>
          )}

          {run.isError && <ErrorNote message={errorMessage(run.error)} />}
        </section>

        {/* ---------------- results side ---------------- */}
        <section className="bench-results">
          {!result && !run.isPending && (
            <div className="panel empty-panel">
              <div className="empty-icon">🔬</div>
              <div className="empty-title">Results appear here</div>
              <div className="empty-hint">
                Every stage is shown: the text read from your file, the lines
                extracted from it, and the catalog products they matched.
              </div>
            </div>
          )}

          {result && (
            <>
              <StageText result={result} />
              <StageMatches result={result} />
            </>
          )}
        </section>
      </div>
    </div>
  )
}

function StageText({ result }) {
  const { text, timings_ms: timings, providers } = result
  const isOcr = text.source === 'ocr'
  const [showBoxes, setShowBoxes] = useState(false)
  const meta = text.meta
  const billing = meta?.billing

  return (
    <div className="panel">
      <div className="panel-head">
        <div className="panel-title">
          2 · {isOcr ? 'Text read from the image' : 'Transcript'}
        </div>
        <div className="stage-meta">
          <span className="chip-static">{text.engine}</span>
          {isOcr && <ConfidenceBadge value={text.mean_confidence} />}
          {text.language && <span className="chip-static">{text.language}</span>}
          {billing?.cost != null && (
            <span className="cost-badge" title={billing.note}>
              ₹{billing.cost.toFixed(4)}
            </span>
          )}
          <span className="muted">{timings.media_to_text_ms} ms</span>
        </div>
      </div>

      {meta?.warning && <div className="banner warn">⚠️ {meta.warning}</div>}
      {billing && <BillingPanel billing={billing} />}

      <pre className="bench-text">{text.value || '(nothing read)'}</pre>

      {meta && <ProviderDetails meta={meta} />}

      {isOcr && text.low_confidence_words?.length > 0 && (
        <div className="banner warn">
          Dropped {text.low_confidence_words.length} word
          {text.low_confidence_words.length > 1 ? 's' : ''} below the confidence
          floor: <code>{text.low_confidence_words.join(', ')}</code>
        </div>
      )}

      {isOcr && text.boxes?.length > 0 && (
        <>
          <button
            type="button"
            className="btn btn-sm stage-toggle"
            onClick={() => setShowBoxes((v) => !v)}
          >
            {showBoxes ? 'Hide' : 'Show'} per-word confidence ({text.boxes.length})
          </button>
          {showBoxes && (
            <table className="table compact">
              <thead>
                <tr>
                  <th>Word</th>
                  <th className="right">Confidence</th>
                  <th className="right">x</th>
                  <th className="right">y</th>
                </tr>
              </thead>
              <tbody>
                {text.boxes.map((b, i) => (
                  <tr key={i} className={b.confidence < 0.7 ? 'row-danger' : ''}>
                    <td className="mono">{b.text}</td>
                    <td className="right">{percent(b.confidence)}</td>
                    <td className="right muted">{b.x}</td>
                    <td className="right muted">{b.y}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      <p className="hint">
        Providers: OCR <code>{providers.ocr}</code> · STT <code>{providers.stt}</code> ·
        extraction <code>{providers.extraction}</code> · auto-match above{' '}
        <code>{providers.auto_threshold}</code>
      </p>
    </div>
  )
}

/**
 * What this one request cost, and how that figure was arrived at.
 * Handles both billing shapes: audio is priced per second of duration,
 * documents per page.
 */
function BillingPanel({ billing }) {
  const hasCost = billing.cost != null
  const perPage = billing.rate_per_page != null

  return (
    <div className="billing">
      <div className="billing-main">
        <div className="billing-cost">
          {hasCost ? `₹${billing.cost.toFixed(4)}` : 'cost unknown'}
        </div>
        <div className="billing-label">this request</div>
      </div>

      <dl className="billing-breakdown">
        {perPage ? (
          <>
            <dt>Pages</dt>
            <dd>{billing.pages ?? '—'}</dd>

            <dt>Rate</dt>
            <dd>₹{billing.rate_per_page}/page</dd>

            {hasCost && (
              <>
                <dt>₹100 credit buys</dt>
                <dd>~{Math.round(100 / billing.rate_per_page)} pages</dd>
              </>
            )}
          </>
        ) : (
          <>
            <dt>Audio length</dt>
            <dd>
              {billing.duration_seconds != null
                ? `${billing.duration_seconds.toFixed(2)} s`
                : '—'}
            </dd>

            <dt>Billed as</dt>
            <dd>
              {billing.billed_seconds != null ? `${billing.billed_seconds} s` : '—'}
              {billing.billed_seconds != null && billing.duration_seconds != null && (
                <span className="muted"> (rounded up)</span>
              )}
            </dd>

            <dt>Rate</dt>
            <dd>₹{billing.rate_per_hour}/hour</dd>

            {hasCost && (
              <>
                <dt>₹100 credit buys</dt>
                <dd>
                  ~{Math.round(100 / billing.rate_per_hour)} hours ·{' '}
                  {Math.round(100 / billing.cost).toLocaleString('en-IN')} notes this
                  length
                </dd>
              </>
            )}
          </>
        )}
      </dl>

      <p className="billing-note">{billing.note}</p>
    </div>
  )
}

/** Everything the provider returned, including the untouched payload. */
function ProviderDetails({ meta }) {
  const [open, setOpen] = useState(false)
  const ts = meta.timestamps

  return (
    <>
      <button
        type="button"
        className="btn btn-sm stage-toggle"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide' : 'Show'} full API response
      </button>

      {open && (
        <>
          <dl className="kv detail-kv">
            {meta.provider && (
              <>
                <dt>Provider</dt>
                <dd>{meta.provider}</dd>
              </>
            )}
            {meta.model && (
              <>
                <dt>Model</dt>
                <dd className="mono">{meta.model}</dd>
              </>
            )}
            {meta.mode && (
              <>
                <dt>Mode</dt>
                <dd className="mono">{meta.mode}</dd>
              </>
            )}
            {meta.request_id && (
              <>
                <dt>Request ID</dt>
                <dd className="mono">{meta.request_id}</dd>
              </>
            )}
            {meta.job_id && (
              <>
                <dt>Job ID</dt>
                <dd className="mono">{meta.job_id}</dd>
              </>
            )}
            {meta.status && (
              <>
                <dt>Job status</dt>
                <dd>{meta.status}</dd>
              </>
            )}
            {meta.usage && (
              <>
                <dt>Pages</dt>
                <dd>
                  {meta.usage.pages_succeeded}/{meta.usage.pages_total} succeeded
                  {meta.usage.pages_failed > 0 && (
                    <span className="missing"> · {meta.usage.pages_failed} failed</span>
                  )}
                </dd>
              </>
            )}
            {meta.language_code && (
              <>
                <dt>Detected language</dt>
                <dd>
                  {meta.language_code}
                  {meta.language_probability != null && (
                    <span className="muted">
                      {' '}
                      · {percent(meta.language_probability)} confident
                    </span>
                  )}
                </dd>
              </>
            )}
            {meta.http && (
              <>
                <dt>HTTP</dt>
                <dd>
                  {meta.http.status} · {meta.http.latency_ms} ms
                </dd>
              </>
            )}
          </dl>

          {ts?.words?.length > 0 && (
            <>
              <div className="line-label">Timestamps</div>
              <table className="table compact">
                <thead>
                  <tr>
                    <th>Segment</th>
                    <th className="right">Start</th>
                    <th className="right">End</th>
                  </tr>
                </thead>
                <tbody>
                  {ts.words.map((w, i) => (
                    <tr key={i}>
                      <td>{w}</td>
                      <td className="right mono">
                        {ts.start_time_seconds?.[i]?.toFixed(2)}s
                      </td>
                      <td className="right mono">
                        {ts.end_time_seconds?.[i]?.toFixed(2)}s
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {meta.http?.headers && Object.keys(meta.http.headers).length > 0 && (
            <>
              <div className="line-label">Response headers</div>
              <pre className="bench-text small">
                {Object.entries(meta.http.headers)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join('\n')}
              </pre>
            </>
          )}

          <div className="line-label">Raw response body</div>
          <pre className="bench-text small">
            {JSON.stringify(meta.raw_response ?? meta, null, 2)}
          </pre>
        </>
      )}
    </>
  )
}

function StageMatches({ result }) {
  const { matches, totals, extraction, timings_ms: timings } = result

  if (!extraction) {
    return (
      <div className="panel">
        <div className="panel-title">3 · Extraction</div>
        <p className="muted">{result.message ?? 'Nothing to extract.'}</p>
      </div>
    )
  }

  if (!extraction.is_order) {
    return (
      <div className="panel">
        <div className="panel-title">3 · Extraction</div>
        <div className="banner warn">
          Read as <strong>not an order</strong> — a greeting, question or chase-up.
          No order would be created.
        </div>
      </div>
    )
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <div className="panel-title">3 · Lines and matched products</div>
        <div className="stage-meta">
          <span className="muted">
            {timings.extraction_ms} ms extract · {timings.matching_ms} ms match
          </span>
        </div>
      </div>

      {totals.would_need_review ? (
        <div className="banner warn">
          This would land in the <strong>review queue</strong> — at least one
          line is unmatched or uncertain.
        </div>
      ) : (
        <div className="banner ok">
          Every line matched confidently — this would be confirmed automatically.
        </div>
      )}

      {extraction.customer_note && (
        <p className="bench-note">📝 {extraction.customer_note}</p>
      )}

      {matches.length === 0 && <p className="muted">No order lines were extracted.</p>}

      {matches.map((m) => (
        <MatchRow key={m.index} match={m} />
      ))}

      {matches.length > 0 && (
        <div className="totals">
          <div>
            <span>Subtotal (tier {totals.price_tier})</span>
            <strong>{money(totals.subtotal)}</strong>
          </div>
          <div>
            <span>GST</span>
            <strong>{money(totals.tax_total)}</strong>
          </div>
          <div className="grand">
            <span>Total</span>
            <strong>{money(totals.grand_total)}</strong>
          </div>
        </div>
      )}
    </div>
  )
}

function MatchRow({ match: m }) {
  const [open, setOpen] = useState(false)
  const attention = m.match_status === 'unmatched' || m.match_status === 'suggested'

  return (
    <div className={`line-row${attention ? ' attention' : ''}`}>
      <div className="line-main bench-line" onClick={() => setOpen((v) => !v)}>
        <div className="line-heard">
          <div className="line-label">Read</div>
          <div className="line-raw">“{m.raw_text}”</div>
          {m.product_text && m.product_text !== m.raw_text && (
            <div className="muted">as product: {m.product_text}</div>
          )}
        </div>

        <div className="line-arrow">→</div>

        <div className="line-matched">
          <div className="line-label">Matched</div>
          {m.matched ? (
            <div className="line-product">
              {m.matched.item_name}
              <span className="line-code">{m.matched.item_code}</span>
            </div>
          ) : (
            <div className="line-product missing">No product matched</div>
          )}
        </div>

        <div className="line-qty">
          {m.quantity != null ? (
            <>
              {m.quantity} {m.unit.toLowerCase()}
              {m.pricing.quantity_base != null && m.unit !== 'KILOGRAMS' && (
                <span className="muted"> ({m.pricing.quantity_base} kg)</span>
              )}
            </>
          ) : (
            <span className="missing">no quantity</span>
          )}
        </div>

        <div className="line-total">
          {m.pricing.total > 0 ? money(m.pricing.total) : '—'}
        </div>

        <div className="line-flags">
          <MatchBadge status={m.match_status} />
          <ConfidenceBadge value={m.confidence} />
        </div>
      </div>

      {open && (
        <div className="line-detail">
          {m.note && <div className="line-note">📝 {m.note}</div>}

          <div className="conf-breakdown">
            <span>
              extraction <strong>{percent(m.extraction_confidence)}</strong>
            </span>
            <span>→</span>
            <span>
              final <strong>{percent(m.confidence)}</strong>
            </span>
            <span className="muted">(the weakest stage wins)</span>
          </div>

          {m.matched && m.pricing.rate != null && (
            <p className="muted">
              {m.pricing.quantity_base} {m.matched.base_unit.toLowerCase()} ×{' '}
              {money(m.pricing.rate)} = {money(m.pricing.total)}
              {m.matched.inclusive_of_tax
                ? ' (rate includes GST)'
                : ` (+${money(m.pricing.tax)} GST)`}
            </p>
          )}

          {m.candidates?.length > 0 ? (
            <>
              <div className="line-label">Closest products considered</div>
              <table className="table compact">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Code</th>
                    <th className="right">Score</th>
                    <th>Why</th>
                  </tr>
                </thead>
                <tbody>
                  {m.candidates.map((c) => (
                    <tr key={c.product_id}>
                      <td>{c.item_name}</td>
                      <td className="mono">{c.item_code}</td>
                      <td className="right">{percent(c.score)}</td>
                      <td className="muted">{c.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <p className="muted">Nothing in the catalog scored close enough to list.</p>
          )}
        </div>
      )}
    </div>
  )
}
